import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import DraftIndicator from './draft/DraftIndicator';
import { Box } from '@mui/material';
import DraftHistory from './draft/DraftHistory';
import findDifferences from './findDifferencesInDataCollection';
import { useTranslation } from 'react-i18next';

const ExoDraftManager = ({
  dataCollection,
  draftType,
  className,
  sx,
  onChange,
  setDataCollection,
  startDataCollection,
  titleFunction,
  // Without startDataCollection: changes in the first `settleMs` after the
  // data arrives come from prefilling (reply data, signature), not from the
  // user, and don't create a draft.
  settleMs = 0,
  // Save unsaved changes when the form closes (unless isFinishedRef.current
  // says it was submitted).
  saveOnUnmount = false,
  isFinishedRef,
}) => {
  const config = useConfig();
  const { t } = useTranslation();

  const [openSelector, setOpenSelector] = useState(false);
  const [draftIsSaved, setDraftIsSaved] = useState(true);
  const [debouncedDataCollection] = useDebounce(dataCollection, 3000);

  // what counts as "no changes"
  const baselineRef = useRef(startDataCollection);
  const isSettledRef = useRef(!settleMs);
  const settleTimerRef = useRef(null);

  useEffect(() => {
    if (startDataCollection) baselineRef.current = startDataCollection;
  }, [startDataCollection]);

  useEffect(() => {
    if (isSettledRef.current || startDataCollection) return;
    if (!dataCollection || Object.keys(dataCollection).length === 0) return;
    baselineRef.current = dataCollection;
    // a fixed window from the first data, so typing never extends it
    if (!settleTimerRef.current)
      settleTimerRef.current = setTimeout(() => {
        isSettledRef.current = true;
      }, settleMs);
  }, [dataCollection]);

  useEffect(() => () => clearTimeout(settleTimerRef.current), []);

  const hasChanges = (data) =>
    Object.keys(findDifferences(baselineRef.current, data, false)).length !== 0;

  useEffect(() => {
    if (!draftType || !hasChanges(dataCollection)) return;
    updateDraft();
  }, [debouncedDataCollection]);

  useEffect(() => {
    if (draftIsSaved && hasChanges(dataCollection)) setDraftIsSaved(false);
  }, [dataCollection]);

  // A draft stores the form data; the mailbox's folder list (inside an email's
  // credentials) is large and not needed to restore it.
  function draftPayload(data) {
    if (!data?.credentials?.inbox) return data;
    const { inbox, ...credentials } = data.credentials;
    return { ...data, credentials };
  }

  const getTitle = (data) =>
    titleFunction ? titleFunction(data) : data.title || t('Untitled');

  // last saved content, so closing right after a save doesn't save again
  const lastSavedRef = useRef(null);
  const serialize = (data) =>
    JSON.stringify(data, (key, value) => (key === 'draftId' ? undefined : value));

  function createDraft({ keepalive = false } = {}) {
    const data = dataCollection;
    DBRequest({
      config,
      path: 'drafts',
      method: {
        ...API_POST({
          dataCollection: draftPayload(data),
          type: draftType,
          title: getTitle(data),
        }),
        keepalive,
      },
      onResponse: (res) => {
        lastSavedRef.current = serialize(data);
        onChange({ draftId: res.data.id });
      },
      onLoading: (value) => setDraftIsSaved(!value),
    });
  }

  function updateDraft() {
    // create draft if there is no draft at the moment of update
    if (!dataCollection.draftId && !draftIsSaved) return createDraft();
    if (!dataCollection.draftId) return;

    const data = dataCollection;
    DBRequest({
      config,
      path: `drafts/${data.draftId}`,
      method: API_PATCH({
        dataCollection: draftPayload(data),
        title: getTitle(data),
      }),
      onResponse: () => {
        lastSavedRef.current = serialize(data);
      },
      onLoading: (value) => setDraftIsSaved(!value),
      // Only a draft that no longer exists is created again. Any other error
      // (e.g. too large) would fail again and pile up new drafts.
      onError: (error, status) => {
        if (status === 404) createDraft();
        else setDraftIsSaved(false);
      },
    });
  }

  // close: send what the debounce hasn't saved yet
  const latestRef = useRef(dataCollection);
  latestRef.current = dataCollection;
  useEffect(
    () => () => {
      if (!saveOnUnmount || !draftType || isFinishedRef?.current) return;
      const data = latestRef.current;
      if (!data || !hasChanges(data)) return;
      if (serialize(data) === lastSavedRef.current) return;
      DBRequest({
        config,
        path: data.draftId ? `drafts/${data.draftId}` : 'drafts',
        method: {
          ...(data.draftId
            ? API_PATCH({
                dataCollection: draftPayload(data),
                title: getTitle(data),
              })
            : API_POST({
                dataCollection: draftPayload(data),
                type: draftType,
                title: getTitle(data),
              })),
          // the request must survive the form (or the page) going away
          keepalive: true,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // badge count
  const [badgeCount, setBadgeCount] = useState(null);

  return (
    draftType && (
      <Box className={className} sx={sx}>
        <DraftHistory
          onSelect={(data) => {
            setDataCollection(data);
            setOpenSelector(false);
          }}
          draftType={draftType}
          open={openSelector}
          onClose={() => setOpenSelector(false)}
          setBadgeCount={setBadgeCount}
        />
        <DraftIndicator
          draftIsSaved={draftIsSaved}
          onClick={() => setOpenSelector(true)}
          badgeCount={badgeCount}
        />
      </Box>
    )
  );
};

export default ExoDraftManager;
